// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11A fixes applied:
//   FIX 1: Replaced 3 occurrences of var(--bg-card) with var(--bg-elevated) — --bg-card was undefined
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart2, FolderKanban, DollarSign, Calendar, Building2, Plus, X, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, FileText, Download, ShoppingCart, Pencil, Trash2, BookOpen, Upload, Lock } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client.ts';
import apiClient from '../api/client.ts';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useFacility } from '../contexts/FacilityContext.tsx';
import { PdfThumbnailRow } from '../components/PdfViewer.tsx';
import PageFocusToggle from '../components/PageFocusToggle.tsx';
import DatePicker from '../components/DatePicker.tsx';
import Logbooks from './Logbooks.tsx';

function getProxyUrl(url: string): string {
  const baseUrl = import.meta.env.VITE_API_URL ?? '';
  const token = localStorage.getItem('accessToken');
  return `${baseUrl}/scheduled-tasks/report-proxy?url=${encodeURIComponent(url)}&token=${token}`;
}

type ReportTab = 'qapi' | 'compliance' | 'purchase-history' | 'fire-logbook' | 'logbooks';

const TABS: { id: ReportTab; label: string }[] = [
  { id: 'qapi', label: 'QAPI' },
  { id: 'compliance', label: 'Compliance Reports' },
  { id: 'purchase-history', label: 'Purchase History' },
  { id: 'fire-logbook', label: 'Life Safety' },
  { id: 'logbooks', label: 'Logbooks' },
];

type ComplianceRange = 'all' | 'month' | '3months' | 'year';

interface ComplianceReport {
  id: number;
  task_id: number;
  task_name: string;
  frequency: string;
  vendor_name: string | null;
  report_url: string;
  report_name: string | null;
  uploaded_at: string;
}

const QAPI_CATEGORIES = [
  'Quality Improvement',
  'Safety',
  'Compliance',
  'Maintenance',
  'Staff Training',
  'Other',
];

// ─── Custom dark dropdown ─────────────────────────────────────────────────────
function QapiCategorySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLButtonElement>(null);

  function openDrop() {
    if (ref.current) setRect(ref.current.getBoundingClientRect());
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={ref}
        onClick={openDrop}
        style={{
          width: '100%', padding: '8px 12px', boxSizing: 'border-box',
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-elevated)', color: 'var(--text-primary)',
          fontSize: '0.875rem', textAlign: 'left',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        {value}
        <ChevronDown size={14} color="var(--text-muted)" />
      </button>
      {open && rect && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', top: rect.bottom + 2, left: rect.left, width: rect.width, zIndex: 10000,
            border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)', background: 'var(--bg-elevated)', overflow: 'hidden',
          }}
        >
          {QAPI_CATEGORIES.map((cat) => (
            <div
              key={cat}
              onMouseDown={() => { onChange(cat); setOpen(false); }}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: '0.875rem',
                background: cat === value ? 'var(--bg-hover)' : undefined,
                color: 'var(--text-primary)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = cat === value ? 'var(--bg-hover)' : 'transparent'; }}
            >
              {cat}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Add / Edit QAPI Entry Modal ─────────────────────────────────────────────
function AddQapiModal({ onClose, onSaved, entryToEdit }: { onClose: () => void; onSaved: () => void; entryToEdit?: QapiEntry }) {
  const [title, setTitle] = useState(entryToEdit?.title ?? '');
  const [category, setCategory] = useState(entryToEdit?.category ?? 'Quality Improvement');
  const [description, setDescription] = useState(entryToEdit?.description ?? '');
  const [entryDate, setEntryDate] = useState(
    entryToEdit?.entry_date
      ? String(entryToEdit.entry_date).slice(0, 10)
      : new Date().toISOString().slice(0, 10)
  );
  const [outcome, setOutcome] = useState(entryToEdit?.outcome ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      if (entryToEdit) {
        await apiClient.put(`/reports/qapi/${entryToEdit.id}`, { title, category, description, entry_date: entryDate, outcome });
      } else {
        await apiClient.post('/reports/qapi', { title, category, description, entry_date: entryDate, outcome });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520, width: '90vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <h3 className="modal-title">{entryToEdit ? 'Edit QAPI Entry' : 'Add QAPI Entry'}</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: 8, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 6 }}>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Entry title" autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <QapiCategorySelect value={category} onChange={setCategory} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the issue or improvement…" />
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <DatePicker value={entryDate} onChange={(v) => setEntryDate(v)} placeholder="Select a date" />
              </div>
              <div className="form-group">
                <label className="form-label">Outcome (optional)</label>
                <textarea className="form-textarea" rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Result or action taken…" />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Entry'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

interface QapiEntry {
  id: number;
  title: string;
  category: string;
  description: string | null;
  outcome: string | null;
  entry_date: string;
  service_date?: string | null;
  created_by_name: string | null;
  is_auto: boolean;
  created_at: string;
}

interface CompletedProject {
  id: number;
  name: string;
  description: string | null;
  workflow_status: string;
  vendor_name: string | null;
  actual_cost: string | null;
  estimated_cost: string | null;
  completed_at: string | null;
  updated_at: string;
  created_at: string;
}

interface FireLogbookEntry {
  id: number;
  section_number: number;
  source: 'manual' | 'compliance_pm';
  scheduled_task_id: number | null;
  scheduled_task_completion_id: number | null;
  entry_date: string;
  title: string | null;
  notes: string | null;
  result: 'pass' | 'fail' | 'deficiency' | null;
  inspector_name: string | null;
  company: string | null;
  license_number: string | null;
  standard: string | null;
  pdf_url: string | null;
  extra_fields: Record<string, unknown>;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  creator_name?: string | null;
  task_frequency?: string | null;
  task_report_url?: string | null;
  task_report_name?: string | null;
  report_pdf_url?: string | null;
  ai_summary?: string | null;
  entry_date_actual?: string | null;
}

const FIRE_SECTIONS: { num: number; name: string; color: string; frequency: string }[] = [
  { num: 1,  name: 'Fire Drills',           color: '#c8952a', frequency: 'Monthly'   },
  { num: 2,  name: 'Fire Alarm System',     color: '#b8a020', frequency: 'Bi-annual' },
  { num: 3,  name: 'Smoke Detectors',       color: '#d4702a', frequency: 'Monthly'   },
  { num: 4,  name: 'Sprinkler System',      color: '#c0442a', frequency: 'Quarterly' },
  { num: 5,  name: 'Water Tanks',           color: '#b83030', frequency: 'Annual'    },
  { num: 6,  name: 'Fire Pump',             color: '#a030a0', frequency: 'Annual'    },
  { num: 7,  name: 'Kitchen Suppression',   color: '#7030b8', frequency: 'Bi-annual' },
  { num: 8,  name: 'Generator',             color: '#4040c0', frequency: 'Weekly'    },
  { num: 9,  name: 'Emergency Lighting',    color: '#2060b8', frequency: 'Annual'    },
  { num: 10, name: 'Fire Extinguishers',    color: '#2080c0', frequency: 'Annual'    },
  { num: 11, name: 'Exit Signs',            color: '#2090d0', frequency: 'Monthly'   },
  { num: 12, name: 'Smoke / Fire Dampers',  color: '#207878', frequency: '4-year'    },
  { num: 13, name: 'Receptacles',           color: '#208060', frequency: 'Annual'    },
  { num: 14, name: 'Snow / Smoking Policy', color: '#508040', frequency: 'As-needed' },
  { num: 15, name: 'Fire Doors',            color: '#70a030', frequency: 'Quarterly' },
];

function getFreqColor(freq: string | null | undefined): { bg: string; text: string } | null {
  if (!freq) return null;
  const f = freq.toLowerCase().trim();
  if (f === 'weekly') return { bg: 'rgba(236,72,153,0.12)', text: '#ec4899' };
  if (f === 'monthly') return { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' };
  if (f === 'quarterly') return { bg: 'rgba(139,92,246,0.12)', text: '#8b5cf6' };
  // Check bi-/semi- BEFORE annual/yearly to avoid false match
  if (f.startsWith('bi') || f.startsWith('semi')) return { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' };
  if (f === 'annual' || f === 'yearly' || f.includes('annual') || f.includes('year')) return { bg: 'rgba(16,185,129,0.12)', text: '#10b981' };
  return { bg: 'rgba(156,163,175,0.15)', text: '#9ca3af' };
}


function fmtCurrency(val: string | number | null): string {
  if (val === null || val === undefined || val === '') return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return '—';
  const s = String(val).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

function getMonthKey(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMMM yyyy');
  } catch { return 'Unknown'; }
}

// ─── Purchase History ─────────────────────────────────────────────────────────

interface PurchaseHistoryRow {
  product_name: string;
  category: string | null;
  total_quantity: string;
  total_spent: string;
  order_count: string;
  last_ordered: string | null;
}

type PhSortCol = 'product_name' | 'category' | 'total_quantity' | 'total_spent' | 'order_count' | 'last_ordered';

function getPhDateRange(range: '30d' | '3m' | '6m' | '1y' | 'custom', customFrom: string, customTo: string): { startDate: string; endDate: string } {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  if (range === 'custom') return { startDate: customFrom || end, endDate: customTo || end };
  const start = new Date(now);
  if (range === '30d') start.setDate(start.getDate() - 30);
  else if (range === '3m') start.setMonth(start.getMonth() - 3);
  else if (range === '6m') start.setMonth(start.getMonth() - 6);
  else start.setFullYear(start.getFullYear() - 1);
  return { startDate: start.toISOString().slice(0, 10), endDate: end };
}

function fmtCurrencyDec(val: string | number | null): string {
  if (val === null || val === undefined || val === '') return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function exportPhCsv(rows: PurchaseHistoryRow[]) {
  const esc = (v: string | number | null | undefined) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ['Product', 'Category', 'Qty Purchased', 'Total Spent', 'Orders', 'Last Ordered'];
  const lines = rows.map(r => [
    r.product_name, r.category ?? '', r.total_quantity,
    parseFloat(r.total_spent || '0').toFixed(2),
    r.order_count, r.last_ordered ? fmtDate(r.last_ordered) : '',
  ].map(esc).join(','));
  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'purchase-history.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ─── Fire Logbook: Add / Edit modal component ────────────────────────────────
function FlLogbookFormModal({
  entryToEdit, initialSection, onClose, onSaved,
}: {
  entryToEdit: FireLogbookEntry | null;
  initialSection: number;
  onClose: () => void;
  onSaved: (sectionNum: number, newId: number | null) => void;
}) {
  const isEdit = entryToEdit != null;
  const [form, setForm] = useState({
    section_number: isEdit ? String(entryToEdit!.section_number) : String(initialSection),
    entry_date: isEdit ? entryToEdit!.entry_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    title: isEdit ? (entryToEdit!.title ?? '') : '',
    notes: isEdit ? (entryToEdit!.notes ?? '') : '',
    result: isEdit ? (entryToEdit!.result ?? '') : '',
    inspector_name: isEdit ? (entryToEdit!.inspector_name ?? '') : '',
    company: isEdit ? (entryToEdit!.company ?? '') : '',
    license_number: isEdit ? (entryToEdit!.license_number ?? '') : '',
    standard: isEdit ? (entryToEdit!.standard ?? '') : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.entry_date) { setError('Date is required'); return; }
    setSaving(true); setError(null);
    try {
      const body = {
        section_number: Number(form.section_number),
        entry_date: form.entry_date,
        title: form.title || undefined,
        notes: form.notes || undefined,
        result: form.result || undefined,
        inspector_name: form.inspector_name || undefined,
        company: form.company || undefined,
        license_number: form.license_number || undefined,
        standard: form.standard || undefined,
      };
      if (isEdit) {
        await apiClient.patch(`/fire-logbook/${entryToEdit!.id}`, body);
        onSaved(Number(form.section_number), null);
      } else {
        const created = await apiClient.post('/fire-logbook', body);
        const newId = created?.data?.data?.id ?? null;
        onSaved(Number(form.section_number), newId);
      }
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e2?.response?.data?.error ?? e2?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540, width: '92vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 className="modal-title">{isEdit ? 'Edit Logbook Entry' : 'Add Logbook Entry'}</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {error && (
                <div style={{ color: '#ef4444', fontSize: '0.875rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 6 }}>{error}</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Section *</label>
                  <select className="form-input" value={form.section_number} onChange={(e) => setForm((f) => ({ ...f, section_number: e.target.value }))}>
                    {FIRE_SECTIONS.map((s) => (
                      <option key={s.num} value={s.num}>{s.num}. {s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Date *</label>
                  <DatePicker value={form.entry_date} onChange={(v) => setForm((f) => ({ ...f, entry_date: v }))} placeholder="Select a date" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input className="form-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Brief description of inspection or drill…" autoFocus={!isEdit} />
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Additional details, findings, or corrective actions…" style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Result</label>
                  <select className="form-input" value={form.result} onChange={(e) => setForm((f) => ({ ...f, result: e.target.value }))}>
                    {[{ value: '', label: 'None' }, { value: 'pass', label: 'Pass' }, { value: 'fail', label: 'Fail' }, { value: 'deficiency', label: 'Deficiency' }].map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Standard / Code</label>
                  <input className="form-input" value={form.standard} onChange={(e) => setForm((f) => ({ ...f, standard: e.target.value }))} placeholder="e.g. NFPA 101" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Inspector Name</label>
                  <input className="form-input" value={form.inspector_name} onChange={(e) => setForm((f) => ({ ...f, inspector_name: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Company</label>
                  <input className="form-input" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">License #</label>
                <input className="form-input" value={form.license_number} onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Entry'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Fire Logbook: PDF upload modal component ─────────────────────────────────
function FlPdfUploadModal({
  entryId, onClose, onUploaded,
}: {
  entryId: number;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true); setUploadErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiClient.post(`/fire-logbook/${entryId}/upload`, fd);
      onUploaded();
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { error?: string } }; message?: string };
      setUploadErr(e2?.response?.data?.error ?? e2?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, width: '90vw' }}>
        <div className="modal-header">
          <h3 className="modal-title">Attach PDF</h3>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ padding: '16px 0 4px' }}>
          {uploadErr && (
            <div style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 6 }}>{uploadErr}</div>
          )}
          <div className="form-group">
            <label className="form-label">Select PDF or document</label>
            <input ref={fileRef} type="file" className="form-input" accept=".pdf,.doc,.docx" />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn btn-ghost" onClick={onClose}>Skip</button>
            <button className="btn btn-primary" disabled={uploading} onClick={handleUpload}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const { facilityName } = useFacility();
  const canManageQapi = ['admin', 'org_admin', 'manager'].includes(user?.role ?? '');
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ReportTab>(() => {
    const saved = sessionStorage.getItem('reports-active-tab') as ReportTab | null;
    return saved ?? (searchParams.get('tab') as ReportTab) ?? 'qapi';
  });
  const [addQapiOpen, setAddQapiOpen] = useState(false);
  const [editQapiEntry, setEditQapiEntry] = useState<QapiEntry | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [complianceRange, setComplianceRange] = useState<ComplianceRange>('all');
  const queryClient = useQueryClient();

  // ─── QAPI filter state ────────────────────────────────────────────────────
  const [filterMonth, setFilterMonth] = useState<number>(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());

  // ─── Project completed-date edit state (admin only) ──────────────────────
  const [editProjectId, setEditProjectId] = useState<number | null>(null);
  const [editProjectDate, setEditProjectDate] = useState('');
  const [editProjectSaving, setEditProjectSaving] = useState(false);

  // ─── Fire Logbook state ───────────────────────────────────────────────────
  const [selectedFlSection, setSelectedFlSection] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('logbook-section');
    return saved ? parseInt(saved, 10) : null;
  });
  const [expandedFlYears, setExpandedFlYears] = useState<Set<number>>(() => new Set([new Date().getFullYear()]));
  const [flAddOpen, setFlAddOpen] = useState(false);
  const [flAddSection, setFlAddSection] = useState<number>(1);
  const [flEditEntry, setFlEditEntry] = useState<FireLogbookEntry | null>(null);
  const [flDeleteId, setFlDeleteId] = useState<number | null>(null);
  const [flPdfUploadId, setFlPdfUploadId] = useState<number | null>(null);
  const [flDeleting, setFlDeleting] = useState(false);
  const [flYearFilter, setFlYearFilter] = useState<number | null>(null);
  const [flResultFilter, setFlResultFilter] = useState<string>('all');
  const flBackfillRan = useRef(false);
  const [flAiSummary, setFlAiSummary] = useState<string | null>(null);
  const [flAiLoading, setFlAiLoading] = useState(false);
  const [flDateConflict, setFlDateConflict] = useState<{aiDetectedDate: string; recordedDate: string} | null>(null);
  const [flDateEditId, setFlDateEditId] = useState<number | null>(null);
  const [flDateEditValue, setFlDateEditValue] = useState('');
  const [flDateEditSaving, setFlDateEditSaving] = useState(false);
  const [flHoveredEntryId, setFlHoveredEntryId] = useState<number | null>(null);
  const [selectedFlEntryId, setSelectedFlEntryId] = useState<number | null>(null);

  // ─── Fire Logbook: one-time backfill on first load ────────────────────────
  useEffect(() => {
    if (activeTab !== 'fire-logbook') return;
    if (flBackfillRan.current) return;
    flBackfillRan.current = true;
    apiClient.post('/fire-logbook/backfill')
      .then(() => queryClient.invalidateQueries({ queryKey: ['fire-logbook'] }))
      .catch(() => {/* non-critical */});
  }, [activeTab]);

  // ─── Clear selected entry when section changes ───────────────────────────
  useEffect(() => {
    setSelectedFlEntryId(null);
  }, [selectedFlSection]);

  // Declared here so it is available to the useEffect below (used before the other useQuery declarations)
  const { data: flRes, isLoading: flLoading } = useQuery({
    queryKey: ['fire-logbook', user?.facilityId],
    queryFn: () => apiClient.get('/fire-logbook'),
    enabled: activeTab === 'fire-logbook',
    refetchOnWindowFocus: true,
  });

  // ─── Pre-populate AI summary from DB when selected entry changes ──────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const allFlEntries: FireLogbookEntry[] = (flRes?.data?.data ?? []) as FireLogbookEntry[];
    const entry = selectedFlEntryId ? allFlEntries.find((e) => e.id === selectedFlEntryId) : null;
    setFlAiSummary(entry?.ai_summary ?? null);
    setFlDateConflict(null);
  }, [selectedFlEntryId, flRes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Print report state ───────────────────────────────────────────────────
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [reportForm, setReportForm] = useState({ preventative: 0, regulatory: 0, safety: 0, other: 0 });
  const mouseDownTarget = useRef<EventTarget | null>(null);

  async function handleDeleteQapi(id: number) {
    try {
      await apiClient.delete(`/reports/qapi/${id}`);
      setDeleteConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['qapi-entries'] });
    } catch (err: any) {
      console.error('QAPI delete error:', err?.message);
    }
  }

  async function handleFlAiSummary(forceRegenerate = false) {
    const allFlEntries: FireLogbookEntry[] = (flRes?.data?.data ?? []) as FireLogbookEntry[];
    const entry = allFlEntries.find((e) => e.id === selectedFlEntryId);
    if (!entry) return;
    setFlAiLoading(true);
    setFlAiSummary(null);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${import.meta.env.VITE_API_URL}/fire-logbook/ai-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          entry_id: entry.id,
          section_number: entry.section_number,
          ...(forceRegenerate ? { force_regenerate: true } : {}),
        }),
      });
      const json = await res.json();
      const summary = json?.data?.summary ?? 'No summary available.';
      setFlAiSummary(summary);
      if (json?.data?.dateConflict) {
        setFlDateConflict({ aiDetectedDate: json.data.aiDetectedDate, recordedDate: json.data.recordedDate });
      } else {
        setFlDateConflict(null);
      }
      // Update RQ cache so the summary shows on re-open without a full refetch
      queryClient.setQueryData(['fire-logbook', user?.facilityId], (old: any) => {
        if (!old?.data?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: (old.data.data as FireLogbookEntry[]).map((ent) =>
              ent.id === selectedFlEntryId ? { ...ent, ai_summary: summary } : ent
            ),
          },
        };
      });
    } catch {
      setFlAiSummary('Failed to generate summary. Please try again.');
    } finally {
      setFlAiLoading(false);
    }
  }

  async function handleFlSaveDateActual(entryId: number) {
    setFlDateEditSaving(true);
    try {
      await apiClient.patch(`/fire-logbook/${entryId}/entry-date`, { entry_date_actual: flDateEditValue });
      queryClient.invalidateQueries({ queryKey: ['fire-logbook'] });
      setFlDateEditId(null);
    } catch { /* ignore */ } finally { setFlDateEditSaving(false); }
  }

  function generateQapiPDF(entries: QapiEntry[], ticketCounts: typeof reportForm, month: number, year: number, projects: CompletedProject[]) {
    const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const monthName = month > 0 ? MONTH_NAMES[month - 1] : 'All Months';
    const totalTickets = ticketCounts.preventative + ticketCounts.regulatory + ticketCounts.safety + ticketCounts.other;

    const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
      'Compliance':           { bg: '#d4e6f1', text: '#1a5276', border: '#1a5276' },
      'Quality Improvement':  { bg: '#d5f5e3', text: '#1e8449', border: '#27ae60' },
      'Safety':               { bg: '#fde8d8', text: '#922b21', border: '#e74c3c' },
      'Preventive':           { bg: '#fef9e7', text: '#9a7d0a', border: '#f1c40f' },
      'Regulatory':           { bg: '#e8daef', text: '#6c3483', border: '#8e44ad' },
      'Other':                { bg: '#eaeded', text: '#4d5656', border: '#95a5a6' },
    };
    function getCategoryColor(cat: string) {
      return categoryColors[cat] ?? categoryColors['Other'];
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>QAPI Report — ${monthName} ${year}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; color: #1a1a1a; padding: 32px; }
    .header { border-bottom: 2px solid #1a5276; padding-bottom: 14px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: flex-start; }
    .header-left { display: flex; align-items: center; gap: 12px; }
    .logo { width: 40px; height: 40px; background: #1a5276; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: 16px; }
    .report-title { font-size: 18px; font-weight: 700; color: #1a5276; }
    .report-sub { font-size: 11px; color: #666; margin-top: 2px; }
    .header-right { font-size: 10px; color: #888; text-align: right; line-height: 1.6; }
    .stats-bar { background: #f4f6f8; border-radius: 6px; padding: 14px 18px; margin-bottom: 18px; display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
    .stat { text-align: center; }
    .stat-n { font-size: 22px; font-weight: 700; color: #1a5276; }
    .stat-l { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 2px; }
    .stat.total .stat-n { color: #2c3e50; border-left: 2px solid #bdc3c7; padding-left: 8px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #1a5276; margin-bottom: 8px; border-bottom: 1px solid #1a5276; padding-bottom: 4px; }
    .entry { padding: 8px 10px; border-left: 3px solid #1a5276; margin-bottom: 6px; background: #f8f9fa; }
    .entry-title { font-size: 11px; font-weight: 700; color: #1a3a5c; }
    .entry-meta { font-size: 9px; color: #666; margin-top: 2px; }
    .entry-desc { font-size: 10px; color: #333; margin-top: 3px; }
    .entry-outcome { display: inline-block; font-size: 8px; font-weight: 700; padding: 2px 6px; border-radius: 3px; margin-top: 4px; }
    .outcome-resolved { background: #d4efdf; color: #1e8449; }
    .outcome-ongoing { background: #fde8d8; color: #c0392b; }
    .proj-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    .proj-table th { padding: 6px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #555; border-bottom: 1px solid #ddd; text-align: left; }
    .proj-table td { padding: 8px 10px; font-size: 10px; color: #333; border-bottom: 1px solid #eee; }
    .proj-table td:first-child { font-weight: 600; color: #1a1a1a; }
    .proj-summary { font-size: 10px; color: #555; margin-bottom: 8px; }
    .sig { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
    .sig-line { border-top: 1px solid #2c3e50; padding-top: 4px; font-size: 9px; color: #555; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <div class="logo">P</div>
      <div>
        <div class="report-title">ProQure — QAPI Monthly Report</div>
        <div class="report-sub">Facility: ${facilityName || 'N/A'} &nbsp;·&nbsp; Period: ${monthName} ${year}</div>
      </div>
    </div>
    <div class="header-right">
      Generated: ${new Date().toLocaleDateString()}<br>
      Total Entries: ${entries.length}
    </div>
  </div>
  <div class="stats-bar">
    <div class="stat"><div class="stat-n" style="color:#27ae60">${ticketCounts.preventative}</div><div class="stat-l">Preventative</div></div>
    <div class="stat"><div class="stat-n" style="color:#8e44ad">${ticketCounts.regulatory}</div><div class="stat-l">Regulatory</div></div>
    <div class="stat"><div class="stat-n" style="color:#e74c3c">${ticketCounts.safety}</div><div class="stat-l">Safety</div></div>
    <div class="stat"><div class="stat-n" style="color:#95a5a6">${ticketCounts.other}</div><div class="stat-l">Other</div></div>
    <div class="stat total"><div class="stat-n" style="color:#2c3e50">${totalTickets}</div><div class="stat-l">Total closed</div></div>
  </div>
  <div class="section-title">QAPI Entries (${entries.length})</div>
  ${entries.map((e, i) => {
    const colors = getCategoryColor(e.category ?? 'Other');
    return `
    <div class="entry" style="border-left: 3px solid ${colors.border}; background: #f8f9fa;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
        <span style="background:${colors.bg};color:${colors.text};font-size:8px;font-weight:700;padding:2px 7px;border-radius:3px;font-family:Arial,sans-serif">${e.category ?? 'Other'}</span>
        <span style="font-size:9px;color:#666;font-family:Arial,sans-serif">${(() => { const s = String(e.service_date ?? e.entry_date).slice(0, 10); const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); })()}</span>
      </div>
      <div class="entry-title">${i + 1}. ${e.title}</div>
      ${e.description ? `<div class="entry-desc">${e.description}</div>` : ''}
      ${e.outcome ? `<span class="entry-outcome ${e.outcome.toLowerCase().includes('resolve') ? 'outcome-resolved' : 'outcome-ongoing'}">${e.outcome}</span>` : ''}
    </div>
  `;
  }).join('')}
  ${projects.length > 0 ? `
  <div class="section-title" style="margin-top: 24px;">Completed Projects (${projects.length})</div>
  <div class="proj-summary">${projects.length} project${projects.length !== 1 ? 's' : ''} &nbsp;·&nbsp; Total spend: $${projects.reduce((s, p) => s + parseFloat(p.actual_cost ?? p.estimated_cost ?? '0'), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
  <table class="proj-table">
    <thead><tr><th>Project</th><th>Vendor</th><th>Final Cost</th><th>Completion Date</th><th>Status</th></tr></thead>
    <tbody>
      ${projects.map(p => `
        <tr>
          <td>${p.name}</td>
          <td>${p.vendor_name ?? '—'}</td>
          <td>$${parseFloat(p.actual_cost ?? p.estimated_cost ?? '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td>${p.completed_at ?? p.updated_at ? new Date(p.completed_at ?? p.updated_at ?? '').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
          <td>${p.workflow_status === 'paid' ? 'Paid' : 'Completed'}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  ` : ''}
  <div class="sig">
    <div class="sig-line">Director of Maintenance &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
    <div class="sig-line">Administrator &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>
  </div>
</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  }

  // ─── Purchase History state ───────────────────────────────────────────────
  const [phRange, setPhRange] = useState<'30d' | '3m' | '6m' | '1y' | 'custom'>('1y');
  const [phCustomFrom, setPhCustomFrom] = useState('');
  const [phCustomTo, setPhCustomTo] = useState('');
  const [phCategoryFilter, setPhCategoryFilter] = useState('');
  const [phSortCol, setPhSortCol] = useState<PhSortCol>('total_quantity');
  const [phSortDir, setPhSortDir] = useState<'asc' | 'desc'>('desc');
  const [phCatDropOpen, setPhCatDropOpen] = useState(false);
  const [phCatDropRect, setPhCatDropRect] = useState<DOMRect | null>(null);
  const phCatBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!phCatDropOpen) return;
    const close = () => setPhCatDropOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [phCatDropOpen]);

  const { startDate: phStart, endDate: phEnd } = getPhDateRange(phRange, phCustomFrom, phCustomTo);

  const { data: phRes, isLoading: phLoading } = useQuery({
    queryKey: ['purchase-history', phStart, phEnd, phCategoryFilter],
    queryFn: () => api.get<PurchaseHistoryRow[]>('/orders/purchase-history', {
      startDate: phStart,
      endDate: phEnd,
      ...(phCategoryFilter ? { categoryFilter: phCategoryFilter } : {}),
    }),
    enabled: activeTab === 'purchase-history',
  });

  const phRows: PurchaseHistoryRow[] = (phRes?.data ?? []) as PurchaseHistoryRow[];
  const phCategories = Array.from(new Set(phRows.map(r => r.category).filter(Boolean))) as string[];
  const phSorted = [...phRows].sort((a, b) => {
    let av: string | number = '';
    let bv: string | number = '';
    if (phSortCol === 'product_name') { av = a.product_name; bv = b.product_name; }
    else if (phSortCol === 'category') { av = a.category ?? ''; bv = b.category ?? ''; }
    else if (phSortCol === 'total_quantity') { av = parseInt(a.total_quantity); bv = parseInt(b.total_quantity); }
    else if (phSortCol === 'total_spent') { av = parseFloat(a.total_spent); bv = parseFloat(b.total_spent); }
    else if (phSortCol === 'order_count') { av = parseInt(a.order_count); bv = parseInt(b.order_count); }
    else if (phSortCol === 'last_ordered') { av = a.last_ordered ?? ''; bv = b.last_ordered ?? ''; }
    if (av < bv) return phSortDir === 'asc' ? -1 : 1;
    if (av > bv) return phSortDir === 'asc' ? 1 : -1;
    return 0;
  });
  function phSort(col: PhSortCol) {
    if (phSortCol === col) setPhSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setPhSortCol(col); setPhSortDir('desc'); }
  }
  const phTotalQty = phRows.reduce((s, r) => s + parseInt(r.total_quantity || '0'), 0);
  const phTotalSpent = phRows.reduce((s, r) => s + parseFloat(r.total_spent || '0'), 0);

  const { data: projectsRes, isLoading } = useQuery({
    queryKey: ['completed-projects-qapi'],
    queryFn: () => api.get<CompletedProject[]>('/projects', { status: 'all' }),
    enabled: activeTab === 'qapi',
  });

  const { data: qapiEntriesRes, isLoading: qapiLoading } = useQuery({
    queryKey: ['qapi-entries', user?.facilityId, filterMonth, filterYear],
    queryFn: () => {
      const params = filterMonth > 0
        ? `?month=${filterMonth}&year=${filterYear}`
        : `?year=${filterYear}`;
      return apiClient.get(`/reports/qapi${params}`);
    },
    enabled: activeTab === 'qapi',
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });
  const manualEntries: QapiEntry[] = (qapiEntriesRes?.data?.data ?? []) as QapiEntry[];

  const { data: complianceRes, isLoading: complianceLoading } = useQuery({
    queryKey: ['compliance-reports', complianceRange],
    queryFn: () => api.get<ComplianceReport[]>(`/scheduled-tasks/reports${complianceRange !== 'all' ? `?range=${complianceRange}` : ''}`),
    enabled: activeTab === 'compliance',
  });

  const allProjects: CompletedProject[] = (projectsRes?.data ?? []) as CompletedProject[];
  const completedProjects = allProjects.filter(
    (p) => p.workflow_status === 'completed' || p.workflow_status === 'paid'
  );

  // Filter completed projects by selected month/year
  const filteredCompletedProjects = completedProjects.filter(p => {
    const dateStr = p.completed_at ?? p.updated_at;
    if (!dateStr) return true;
    const d = new Date(dateStr);
    if (filterYear && d.getUTCFullYear() !== filterYear) return false;
    if (filterMonth > 0 && d.getUTCMonth() + 1 !== filterMonth) return false;
    return true;
  });

  return (
    <div>
      {/* Focus mode toggle — same floating-action-cluster (AppLayout.css) + PageFocusToggle
          Dashboard.tsx uses. */}
      <div className="floating-action-cluster"><PageFocusToggle /></div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Quality Assurance &amp; Compliance Reports</p>
        </div>
        {activeTab === 'qapi' && (
          <div style={{ display: 'flex', gap: 8 }}>
            {canManageQapi && (
              <button className="btn btn-ghost" onClick={() => setPrintModalOpen(true)} style={{ gap: 6 }}>
                <FileText size={16} /> Print Report
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setAddQapiOpen(true)} style={{ gap: 6 }}>
              <Plus size={16} /> Add QAPI Entry
            </button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 0 }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); sessionStorage.setItem('reports-active-tab', tab.id); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-muted)',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent',
              marginBottom: -1,
              transition: 'color 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Logbooks tab — fully separate system from Life Safety/fire-logbook, per instruction.
          Disaster Manual + Filled Logs shell, own component file (client/src/pages/Logbooks.tsx). */}
      {activeTab === 'logbooks' && <Logbooks />}

      {activeTab === 'qapi' && (() => {
        // Filtering is done server-side (month/year passed as query params)
        const filteredQapiEntries = manualEntries;
        return (
        <>
          {/* ── Month / Year filter row ──────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <select
              value={filterMonth || ''}
              onChange={e => setFilterMonth(e.target.value ? Number(e.target.value) : 0)}
              className="form-input"
              style={{ width: 140 }}
            >
              <option value="">All months</option>
              {['January','February','March','April','May','June','July','August','September','October','November','December']
                .map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select
              value={filterYear}
              onChange={e => setFilterYear(Number(e.target.value))}
              className="form-input"
              style={{ width: 100 }}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)
                .map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {filterMonth > 0 && (
              <button onClick={() => setFilterMonth(0)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                × Clear
              </button>
            )}
          </div>

          {/* ── Manual Entries section ───────────────────────────────────────── */}
          <div style={{ marginBottom: 36 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 8, borderBottom: '2px solid var(--border-subtle)' }}>
              <BarChart2 size={16} style={{ color: 'var(--accent-primary)' }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>Manual Entries</h2>
            </div>
            {qapiLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="loading-spinner" />
              </div>
            ) : filteredQapiEntries.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {manualEntries.length === 0 ? 'No manual entries yet. Click "Add QAPI Entry" to create one.' : 'No entries match the selected filter.'}
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                      {['Title', 'Category', 'Date', 'Outcome'].map((h) => (
                        <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                      <th style={{ padding: '8px 14px', width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQapiEntries.map((entry) => (
                      <tr key={entry.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                          {entry.title}
                          {entry.description && (
                            <div style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{entry.description}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span className="badge badge-purple">{entry.category}</span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {fmtDate(entry.service_date ?? entry.entry_date)}
                          {!entry.service_date && (
                            <span title="Upload date — set service date in Compliance PM" style={{ fontSize: 9, opacity: 0.6, color: '#f59e0b', marginLeft: 2 }}>*</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{entry.outcome ?? '—'}</td>
                        <td style={{ padding: '12px 14px', width: 80 }}>
                          {canManageQapi && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                onClick={() => setEditQapiEntry(entry)}
                                style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(entry.id)}
                                style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Completed Projects section ───────────────────────────────────── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 8, borderBottom: '2px solid var(--border-subtle)' }}>
              <FolderKanban size={16} style={{ color: 'var(--accent-primary)' }} />
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>Completed Projects</h2>
            </div>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <div className="loading-spinner" />
              </div>
            ) : filteredCompletedProjects.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {completedProjects.length === 0 ? 'No completed projects yet.' : 'No completed projects match the selected filter.'}
              </div>
            ) : (() => {
              const totalSpend = filteredCompletedProjects.reduce((sum, p) => {
                const cost = p.actual_cost ?? p.estimated_cost;
                return sum + (cost ? parseFloat(cost) : 0);
              }, 0);
              return (
                <>
                  {/* Summary line */}
                  <div style={{ display: 'flex', gap: 20, marginBottom: 12, fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FolderKanban size={13} />
                      {filteredCompletedProjects.length} project{filteredCompletedProjects.length !== 1 ? 's' : ''} completed
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <DollarSign size={13} />
                      Total spend: <strong style={{ color: 'var(--text-primary)' }}>{fmtCurrency(totalSpend)}</strong>
                    </span>
                  </div>
                  <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                          {['Project', 'Vendor', 'Final Cost', 'Completion Date', 'Status'].map((h) => (
                            <th key={h} style={{
                              padding: '8px 14px', textAlign: 'left',
                              fontSize: '0.7rem', fontWeight: 700,
                              color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCompletedProjects.map((p) => {
                          const completionDate = p.completed_at ?? p.updated_at;
                          const isFullyPaid = p.workflow_status === 'paid';
                          return (
                            <tr key={p.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                              <td style={{ padding: '12px 14px' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>{p.name}</div>
                                {p.description && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</div>
                                )}
                              </td>
                              <td style={{ padding: '12px 14px' }}>
                                {p.vendor_name ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                    <Building2 size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                    {p.vendor_name}
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>—</span>
                                )}
                              </td>
                              <td style={{ padding: '12px 14px' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                                  {fmtCurrency(p.actual_cost ?? p.estimated_cost)}
                                </div>
                                {p.actual_cost && p.estimated_cost && Math.abs(parseFloat(p.actual_cost) - parseFloat(p.estimated_cost)) > 1 && (
                                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                                    Est: {fmtCurrency(p.estimated_cost)}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  {fmtDate(completionDate)}
                                  {user?.role === 'admin' && (
                                    <button
                                      onClick={() => {
                                        const d = completionDate ? completionDate.slice(0, 10) : new Date().toISOString().slice(0, 10);
                                        setEditProjectDate(d);
                                        setEditProjectId(p.id);
                                      }}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', borderRadius: 4, transition: 'color var(--transition)' }}
                                      title="Edit completion date"
                                    >
                                      <Pencil size={13} />
                                    </button>
                                  )}
                                </span>
                              </td>
                              <td style={{ padding: '12px 14px' }}>
                                <span className={isFullyPaid ? 'badge badge-green' : 'badge badge-blue'}>
                                  {isFullyPaid ? 'Paid' : 'Completed'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        </>
      );
      })()}

      {activeTab === 'compliance' && (
        <>
          {/* Range filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {([
              { id: 'all', label: 'All' },
              { id: 'month', label: 'This Month' },
              { id: '3months', label: 'Last 3 Months' },
              { id: 'year', label: 'This Year' },
            ] as { id: ComplianceRange; label: string }[]).map(r => (
              <button
                key={r.id}
                onClick={() => setComplianceRange(r.id)}
                style={{
                  padding: '5px 14px', borderRadius: 999, border: '1px solid var(--border-default)',
                  background: complianceRange === r.id ? 'var(--accent-primary)' : 'transparent',
                  color: complianceRange === r.id ? '#fff' : 'var(--text-secondary)',
                  fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
                }}
              >{r.label}</button>
            ))}
          </div>

          {complianceLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="loading-spinner" />
            </div>
          ) : !complianceRes?.data || (complianceRes.data as ComplianceReport[]).length === 0 ? (
            <div style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)', padding: 48,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center',
            }}>
              <FileText size={48} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.125rem' }}>No compliance reports yet</h3>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem', maxWidth: 420 }}>
                Attach reports to scheduled maintenance tasks to see them here.
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                    {['Task Name', 'File', 'Vendor', 'Upload Date', 'Report'].map(h => (
                      <th key={h} style={{
                        padding: '8px 14px', textAlign: 'left',
                        fontSize: '0.7rem', fontWeight: 700,
                        color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(complianceRes.data as ComplianceReport[]).map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                        {r.task_name}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FileText size={13} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                          {r.report_name ?? `Report - ${new Date(r.uploaded_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })}`}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {r.vendor_name ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Building2 size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            {r.vendor_name}
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {new Date(r.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => window.open(getProxyUrl(r.report_url), '_blank', 'noopener,noreferrer')}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <ExternalLink size={12} /> View Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'purchase-history' && (
        <>
          {/* Controls row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {([
                { id: '30d', label: '30 Days' },
                { id: '3m',  label: '3 Months' },
                { id: '6m',  label: '6 Months' },
                { id: '1y',  label: '1 Year' },
                { id: 'custom', label: 'Custom' },
              ] as { id: typeof phRange; label: string }[]).map(r => (
                <button key={r.id} onClick={() => setPhRange(r.id)} style={{
                  padding: '5px 14px', borderRadius: 999, border: '1px solid var(--border-default)',
                  background: phRange === r.id ? 'var(--accent-primary)' : 'transparent',
                  color: phRange === r.id ? '#fff' : 'var(--text-secondary)',
                  fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
                }}>{r.label}</button>
              ))}
            </div>
            {phRange === 'custom' && (
              <>
                <div style={{ width: 148 }}>
                  <DatePicker value={phCustomFrom} onChange={v => setPhCustomFrom(v)} placeholder="From" style={{ padding: '5px 10px', fontSize: '0.8125rem' }} />
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>to</span>
                <div style={{ width: 148 }}>
                  <DatePicker value={phCustomTo} onChange={v => setPhCustomTo(v)} placeholder="To" style={{ padding: '5px 10px', fontSize: '0.8125rem' }} />
                </div>
              </>
            )}
            {/* Category filter dropdown */}
            <div style={{ marginLeft: 'auto' }}>
              <button
                type="button"
                ref={phCatBtnRef}
                onClick={() => {
                  if (phCatBtnRef.current) setPhCatDropRect(phCatBtnRef.current.getBoundingClientRect());
                  setPhCatDropOpen(v => !v);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px',
                  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-elevated)', color: phCategoryFilter ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontSize: '0.8125rem', cursor: 'pointer', minWidth: 150,
                }}
              >
                {phCategoryFilter || 'All Categories'}
                <ChevronDown size={13} style={{ marginLeft: 'auto' }} />
              </button>
              {phCatDropOpen && phCatDropRect && (
                <div
                  onMouseDown={e => e.stopPropagation()}
                  style={{
                    position: 'fixed', top: phCatDropRect.bottom + 2, left: phCatDropRect.left,
                    width: Math.max(phCatDropRect.width, 180), zIndex: 10000,
                    border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-md)', background: 'var(--bg-elevated)', overflow: 'hidden',
                  }}
                >
                  {(['', ...phCategories]).map(cat => (
                    <div key={cat || '__all'}
                      onMouseDown={() => { setPhCategoryFilter(cat); setPhCatDropOpen(false); }}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', fontSize: '0.8125rem',
                        background: cat === phCategoryFilter ? 'var(--bg-hover)' : undefined,
                        color: 'var(--text-primary)',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = cat === phCategoryFilter ? 'var(--bg-hover)' : 'transparent'; }}
                    >
                      {cat || 'All Categories'}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Export CSV */}
            <button
              onClick={() => exportPhCsv(phSorted)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px',
                border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                background: 'transparent', color: 'var(--text-secondary)',
                fontSize: '0.8125rem', cursor: 'pointer',
              }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          {phLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="loading-spinner" />
            </div>
          ) : phSorted.length === 0 ? (
            <div style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)', padding: 48,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center',
            }}>
              <ShoppingCart size={48} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.125rem' }}>No purchase history found for this period.</h3>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
                <div className="kpi-card">
                  <div className="kpi-label">Total Items Purchased</div>
                  <div className="kpi-value" style={{ color: '#3b82f6' }}>{phTotalQty.toLocaleString()}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Total Spent</div>
                  <div className="kpi-value" style={{ color: '#10b981' }}>{fmtCurrencyDec(phTotalSpent)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Unique Products</div>
                  <div className="kpi-value">{phRows.length}</div>
                </div>
              </div>

              {/* Table */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                      {([
                        { key: 'product_name',   label: 'Product' },
                        { key: 'category',       label: 'Category' },
                        { key: 'total_quantity', label: 'Qty Purchased' },
                        { key: 'total_spent',    label: 'Total Spent' },
                        { key: 'order_count',    label: 'Orders' },
                        { key: 'last_ordered',   label: 'Last Ordered' },
                      ] as { key: PhSortCol; label: string }[]).map(col => (
                        <th key={col.key} onClick={() => phSort(col.key)} style={{
                          padding: '8px 14px', textAlign: 'left', cursor: 'pointer',
                          fontSize: '0.7rem', fontWeight: 700,
                          color: phSortCol === col.key ? 'var(--text-primary)' : 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.05em', userSelect: 'none',
                        }}>
                          {col.label}{phSortCol === col.key ? (phSortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {phSorted.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                          {r.product_name}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {r.category ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                              fontSize: '0.75rem', fontWeight: 500,
                              background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                              border: '1px solid var(--border-subtle)',
                            }}>{r.category}</span>
                          ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: '1rem', color: '#3b82f6' }}>
                          {parseInt(r.total_quantity || '0').toLocaleString()}
                        </td>
                        <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.875rem', color: '#10b981' }}>
                          {fmtCurrencyDec(r.total_spent)}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {r.order_count}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {r.last_ordered ? fmtDate(r.last_ordered) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'fire-logbook' && (() => {
        const allFlEntries: FireLogbookEntry[] = (flRes?.data?.data ?? []) as FireLogbookEntry[];

        // All years present in data, plus always include current year
        const currentYear = new Date().getFullYear();
        const dataYears = Array.from(new Set(
          allFlEntries.map((e) => new Date(e.entry_date.slice(0, 10) + 'T12:00:00Z').getUTCFullYear())
        ));
        const allYears = Array.from(new Set([currentYear, ...dataYears])).sort((a, b) => b - a);

        // Available years for filter dropdown
        const filterYears = allYears;
        const activeYear = flYearFilter ?? currentYear;

        // Entries filtered for the right panel
        const sectionEntries = selectedFlSection != null
          ? allFlEntries.filter((e) => {
              if (Number(e.section_number) !== Number(selectedFlSection)) return false;
              const yr = new Date(e.entry_date.slice(0, 10) + 'T12:00:00Z').getUTCFullYear();
              if (yr !== activeYear) return false;
              if (flResultFilter !== 'all' && e.result !== flResultFilter) return false;
              return true;
            })
          : [];

        function toggleFlYear(yr: number) {
          setExpandedFlYears((prev) => {
            const next = new Set(prev);
            next.has(yr) ? next.delete(yr) : next.add(yr);
            return next;
          });
        }

        const sec = selectedFlSection != null ? FIRE_SECTIONS.find((s) => s.num === selectedFlSection) ?? null : null;
        const freqStyle = sec ? getFreqColor(sec.frequency) : null;
        const allSectionEntries = selectedFlSection != null
          ? allFlEntries.filter((e) => {
              if (Number(e.section_number) !== Number(selectedFlSection)) return false;
              const yr = new Date(e.entry_date.slice(0, 10) + 'T12:00:00Z').getUTCFullYear();
              return yr === activeYear;
            })
          : [];
        const selectedEntry = selectedFlEntryId != null
          ? allFlEntries.find((e) => e.id === selectedFlEntryId) ?? null
          : null;
        const entryPanelOpen = selectedEntry != null;

        return (
          <div style={{ display: 'flex', height: 'calc(100vh - 210px)', overflow: 'hidden', position: 'relative' }}>
            {/* ── Left Panel: Section Index (280px) ────────────────────── */}
            <div style={{
              width: 280, flexShrink: 0,
              borderRight: '1px solid var(--border-subtle)',
              overflowY: 'auto',
            }}>
              {/* Header */}
              <div style={{
                padding: '12px 14px 10px',
                borderBottom: '1px solid var(--border-subtle)',
                fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                15 Sections
              </div>

              {flLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                  <div className="loading-spinner" />
                </div>
              ) : (
                <>
                  {allYears.map((yr) => {
                    const isExpanded = expandedFlYears.has(yr);
                    const yearEntries = allFlEntries.filter((e) => new Date(e.entry_date.slice(0, 10) + 'T12:00:00Z').getUTCFullYear() === yr);
                    // Current year: always show all 15 sections; other years: only sections with entries
                    const sectionsToShow = yr === currentYear
                      ? FIRE_SECTIONS
                      : FIRE_SECTIONS.filter((s) => yearEntries.some((e) => Number(e.section_number) === Number(s.num)));
                    if (sectionsToShow.length === 0) return null;
                    return (
                      <div key={yr}>
                        <button
                          onClick={() => toggleFlYear(yr)}
                          style={{
                            display: 'flex', width: '100%', alignItems: 'center', gap: 6,
                            padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                            color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                          }}
                        >
                          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          {yr}
                          <span style={{
                            marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 700,
                            background: 'var(--bg-elevated)', padding: '1px 6px',
                            borderRadius: 999, border: '1px solid var(--border-subtle)', color: 'var(--text-muted)',
                          }}>
                            {yearEntries.length}
                          </span>
                        </button>
                        {isExpanded && sectionsToShow.map((s) => {
                          const count = yearEntries.filter((e) => Number(e.section_number) === Number(s.num)).length;
                          const fs = getFreqColor(s.frequency);
                          const isActive = selectedFlSection === s.num && (flYearFilter ?? currentYear) === yr;
                          return (
                            <button
                              key={s.num}
                              onClick={() => {
                                setSelectedFlSection(s.num);
                                sessionStorage.setItem('logbook-section', String(s.num));
                                setFlAddSection(s.num);
                                setFlYearFilter(yr === currentYear ? null : yr);
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px 9px 22px',
                                width: '100%', border: 'none', textAlign: 'left', cursor: 'pointer',
                                background: isActive ? 'rgba(59,130,246,0.08)' : 'transparent',
                                borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                                transition: 'background 0.1s',
                              }}
                            >
                              <span style={{
                                minWidth: 26, height: 26, borderRadius: 6, flexShrink: 0,
                                background: s.color, color: '#fff',
                                fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>{s.num}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: isActive ? 600 : 400, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {s.name}
                                </div>
                                {fs && (
                                  <span style={{
                                    display: 'inline-block', marginTop: 2, padding: '1px 6px',
                                    borderRadius: 999, fontSize: '0.62rem', fontWeight: 600,
                                    background: fs.bg, color: fs.text,
                                  }}>
                                    {s.frequency}
                                  </span>
                                )}
                              </div>
                              <span style={{
                                fontSize: '0.65rem', fontWeight: 700, flexShrink: 0, minWidth: 20, textAlign: 'center',
                                background: count > 0 ? s.color : 'var(--bg-elevated)',
                                color: count > 0 ? '#fff' : 'var(--text-muted)',
                                borderRadius: 999, padding: '1px 6px',
                                border: count > 0 ? 'none' : '1px solid var(--border-subtle)',
                              }}>
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* ── Right Panel: Entry List ───────────────────────────────── */}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {selectedFlSection == null ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, color: 'var(--text-muted)' }}>
                  <BookOpen size={52} style={{ opacity: 0.25 }} />
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>Select a section from the sidebar to view entries</p>
                  {canManageQapi && (
                    <button className="btn btn-primary" onClick={() => { setFlAddSection(1); setFlAddOpen(true); }} style={{ gap: 6 }}>
                      <Plus size={16} /> Add Entry
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* Right panel header */}
                  <div style={{ flexShrink: 0, padding: '14px 20px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: sec!.color, color: '#fff',
                        fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{sec!.num}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sec!.name}
                        </div>
                        {freqStyle && (
                          <span style={{ display: 'inline-block', marginTop: 1, padding: '1px 6px', borderRadius: 999, fontSize: '0.62rem', fontWeight: 600, background: freqStyle.bg, color: freqStyle.text }}>
                            {sec!.frequency}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <select
                        value={flYearFilter ?? currentYear}
                        onChange={(e) => setFlYearFilter(Number(e.target.value))}
                        style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        {filterYears.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
                      </select>
                      <select
                        value={flResultFilter}
                        onChange={(e) => setFlResultFilter(e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        <option value="all">All Results</option>
                        <option value="pass">Pass</option>
                        <option value="fail">Fail</option>
                        <option value="deficiency">Deficiency</option>
                      </select>
                      {(user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin') && (
                        <button
                          disabled={flAiLoading}
                          onClick={() => handleFlAiSummary()}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 7, border: 'none', background: '#7c3aed', color: 'white', fontSize: 11, fontWeight: 600, cursor: flAiLoading ? 'default' : 'pointer', opacity: flAiLoading ? 0.7 : 1 }}
                        >
                          {flAiLoading ? 'Analyzing…' : '✦ Analyze this report'}
                        </button>
                      )}
                      {canManageQapi && (
                        <button className="btn btn-primary" onClick={() => { setFlAddSection(selectedFlSection); setFlAddOpen(true); }} style={{ gap: 5, padding: '5px 11px', fontSize: '0.8rem' }}>
                          <Plus size={14} /> Add Entry
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Entry list */}
                  <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    {sectionEntries.length === 0 ? (
                      <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                        No entries for this section in {activeYear}.
                      </div>
                    ) : (
                      sectionEntries.map((entry) => {
                        const isPM = entry.source === 'compliance_pm';
                        const isSelected = entry.id === selectedFlEntryId;
                        const resultDot = entry.result === 'pass' ? '#10b981'
                          : (entry.result === 'fail' || entry.result === 'deficiency') ? '#ef4444'
                          : 'rgba(255,255,255,0.2)';
                        const isHovered = flHoveredEntryId === entry.id;
                        return (
                          <button
                            key={entry.id}
                            onMouseEnter={() => setFlHoveredEntryId(entry.id)}
                            onMouseLeave={() => setFlHoveredEntryId(null)}
                            onClick={() => {
                              setSelectedFlEntryId(entry.id);
                              setFlAiSummary(null);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                              padding: '0 16px', minHeight: 40,
                              border: 'none', textAlign: 'left', cursor: 'pointer',
                              background: isSelected ? 'rgba(59,130,246,0.08)' : isHovered ? 'rgba(255,255,255,0.03)' : 'transparent',
                              borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                              borderBottom: '1px solid var(--border-subtle)',
                              transition: 'background 0.1s',
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                {fmtDate(entry.entry_date_actual ?? entry.entry_date)}
                                {!entry.entry_date_actual && (
                                  <span title="Upload date — set service date in Compliance PM" style={{ fontSize: 9, opacity: 0.6, color: '#f59e0b' }}>*</span>
                                )}
                                {canManageQapi && isHovered && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    title="Edit actual date"
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      setSelectedFlEntryId(entry.id);
                                      setFlAiSummary(null);
                                      setFlDateEditValue((entry.entry_date_actual ?? entry.entry_date).slice(0, 10));
                                      setFlDateEditId(entry.id);
                                    }}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 3,
                                      background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                                      borderRadius: 6, padding: '2px 6px',
                                      cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 10,
                                      userSelect: 'none', lineHeight: 1,
                                    }}
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                    </svg>
                                    Edit date
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                                {entry.title || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Untitled</span>}
                              </div>
                            </div>
                            {isPM && (
                              <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 5px', borderRadius: 999, fontSize: '0.6rem', fontWeight: 600, background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}>
                                <Lock size={8} /> PM
                              </span>
                            )}
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: resultDot, border: '1px solid rgba(255,255,255,0.1)' }} />
                          </button>
                        );
                      })
                    )}
                  </div>

                  {/* Count footer */}
                  <div style={{ flexShrink: 0, padding: '6px 16px', borderTop: '1px solid var(--border-subtle)', fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{allSectionEntries.length} {allSectionEntries.length === 1 ? 'entry' : 'entries'} in {activeYear}</span>
                    <span>{allSectionEntries.filter((e) => e.source === 'compliance_pm').length} from PM · {allSectionEntries.filter((e) => e.source === 'manual').length} manual</span>
                  </div>
                </>
              )}
            </div>

            {/* ── Sliding Entry Detail Panel ────────────────────────────── */}
            {ReactDOM.createPortal(
              <>
                {/* Backdrop */}
                <div
                  onClick={() => { setSelectedFlEntryId(null); setFlAiSummary(null); }}
                  style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', opacity: entryPanelOpen ? 1 : 0, pointerEvents: entryPanelOpen ? 'auto' : 'none', transition: 'opacity 0.25s ease' }}
                />
                {/* Panel */}
                <div style={{
                  position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 999,
                  width: 600,
                  background: 'var(--bg-panel)',
                  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
              display: 'flex', flexDirection: 'column',
              transform: entryPanelOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
            }}>
              {!selectedEntry && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <p style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'center', margin: 0 }}>
                    Select an entry to view details
                  </p>
                </div>
              )}
              {selectedEntry && (() => {
                const e = selectedEntry;
                const isPM = e.source === 'compliance_pm';
                const pmPdfUrl = e.report_pdf_url ?? e.task_report_url ?? e.pdf_url ?? null;
                const rawDisplayUrl = isPM ? pmPdfUrl : e.pdf_url;
                const displayPdfUrl = rawDisplayUrl ? getProxyUrl(rawDisplayUrl) : null;
                const resultColor = e.result === 'pass' ? '#10b981' : e.result === 'fail' ? '#ef4444' : e.result === 'deficiency' ? '#f59e0b' : null;
                return (
                  <>
                    {/* Panel fixed header */}
                    <div style={{ flexShrink: 0, padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0d1526' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ minWidth: 0 }}>
                          {flDateEditId === e.id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <DatePicker
                                value={flDateEditValue}
                                onChange={(v) => setFlDateEditValue(v)}
                                placeholder="Select a date"
                                style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', fontSize: 13 }}
                              />
                              <button
                                onClick={() => handleFlSaveDateActual(e.id)}
                                disabled={flDateEditSaving}
                                style={{ padding: '3px 10px', borderRadius: 5, background: '#3b82f6', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                              >
                                {flDateEditSaving ? '…' : 'Save'}
                              </button>
                              <button
                                onClick={() => setFlDateEditId(null)}
                                style={{ padding: '3px 8px', borderRadius: 5, background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 12 }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
                                {fmtDate(e.entry_date_actual ?? e.entry_date)}
                              </span>
                              {!e.entry_date_actual && (
                                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>(upload date)</span>
                              )}
                              {canManageQapi && (
                                <button
                                  onClick={() => {
                                    setFlDateEditValue((e.entry_date_actual ?? e.entry_date).slice(0, 10));
                                    setFlDateEditId(e.id);
                                  }}
                                  onMouseEnter={(ev) => { ev.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; ev.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                  onMouseLeave={(ev) => { ev.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; ev.currentTarget.style.background = 'transparent'; }}
                                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 11 }}
                                  title="Edit actual date"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                  Edit date
                                </button>
                              )}
                            </div>
                          )}
                          {e.title && (
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>{e.title}</div>
                          )}
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            {resultColor && e.result && (
                              <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: resultColor + '22', color: resultColor, border: `1px solid ${resultColor}44` }}>
                                {e.result.charAt(0).toUpperCase() + e.result.slice(1)}
                              </span>
                            )}
                            {isPM && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600, background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}>
                                <Lock size={10} /> Compliance PM
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => { setSelectedFlEntryId(null); setFlAiSummary(null); }}
                          style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 4 }}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>

                    {/* Scrollable content */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                      {/* Details card */}
                      {(e.notes || e.inspector_name || e.company || e.license_number || e.standard || isPM) && (
                        <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
                          {e.notes && (
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 10, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{e.notes}</div>
                          )}
                          {(e.inspector_name || e.company || e.license_number || e.standard) && (
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                              {e.inspector_name && <span>Inspector: <strong style={{ color: 'var(--text-secondary)' }}>{e.inspector_name}</strong></span>}
                              {e.company && <span>Company: <strong style={{ color: 'var(--text-secondary)' }}>{e.company}</strong></span>}
                              {e.license_number && <span>License #: <strong style={{ color: 'var(--text-secondary)' }}>{e.license_number}</strong></span>}
                              {e.standard && <span>Standard: <strong style={{ color: 'var(--text-secondary)' }}>{e.standard}</strong></span>}
                            </div>
                          )}
                          {isPM && (
                            <div style={{ marginTop: e.notes || e.inspector_name || e.company || e.license_number || e.standard ? 10 : 0 }}>
                              <a
                                href={e.scheduled_task_id ? `/tasks?tab=schedule&highlightTask=${e.scheduled_task_id}` : '/tasks?tab=schedule'}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 5, background: 'rgba(99,102,241,0.08)', color: '#818cf8', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' }}
                              >
                                <ExternalLink size={12} /> View in Compliance PM →
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Attach PDF (manual entries) */}
                      {!isPM && canManageQapi && (
                        <div style={{ marginBottom: 16 }}>
                          <button
                            onClick={() => setFlPdfUploadId(e.id)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 5, border: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-muted)', fontSize: '0.8rem', cursor: 'pointer' }}
                          >
                            <Upload size={12} /> {e.pdf_url ? 'Replace PDF' : 'Attach PDF'}
                          </button>
                        </div>
                      )}

                      {/* PDF thumbnails */}
                      {displayPdfUrl && (
                        <div style={{ marginBottom: 16 }}>
                          <PdfThumbnailRow pdfUrl={displayPdfUrl} />
                        </div>
                      )}

                      {/* AI summary */}
                      {(user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin') && (
                        <div>
                          {flAiSummary && (
                            <div style={{ marginBottom: 12, borderLeft: '3px solid #7c3aed', background: 'rgba(124,58,237,0.05)', borderRadius: 8, padding: '14px 16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontWeight: 500, fontSize: 12, color: '#a78bfa' }}>✦ Report Analysis</span>
                                  <button
                                    onClick={() => handleFlAiSummary(true)}
                                    disabled={flAiLoading}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(167,139,250,0.6)', fontSize: 11, padding: 0, textDecoration: 'underline', opacity: flAiLoading ? 0.5 : 1 }}
                                  >
                                    Re-analyze ↺
                                  </button>
                                </div>
                                <button onClick={() => setFlAiSummary(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                                  <X size={15} />
                                </button>
                              </div>
                              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{flAiSummary}</p>
                              {flDateConflict && (
                                <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6 }}>
                                  <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, marginBottom: 6 }}>
                                    ⚠ The AI detected a date in this report ({flDateConflict.aiDetectedDate}) that differs from the recorded service date ({flDateConflict.recordedDate}). Consider updating the service date.
                                  </div>
                                  <button type="button"
                                    onClick={async () => {
                                      const allEntries = (flRes?.data?.data ?? []) as FireLogbookEntry[];
                                      const entry = allEntries.find((e: FireLogbookEntry) => e.id === selectedFlEntryId);
                                      if (entry && flDateConflict) {
                                        try {
                                          const parsed = new Date(flDateConflict.aiDetectedDate);
                                          const dateStr = !isNaN(parsed.getTime()) ? parsed.toISOString().slice(0, 10) : flDateConflict.aiDetectedDate;
                                          await apiClient.patch(`/fire-logbook/${entry.id}/entry-date`, { entry_date_actual: dateStr });
                                          queryClient.invalidateQueries({ queryKey: ['fire-logbook'] });
                                          setFlDateConflict(null);
                                        } catch { /* silent */ }
                                      }
                                    }}
                                    style={{ background: 'none', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 4, cursor: 'pointer', color: '#f59e0b', padding: '2px 8px', fontSize: 11 }}>
                                    Update to AI date
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Panel footer: actions */}
                    <div style={{ flexShrink: 0, padding: '12px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {(user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise' || user?.role === 'admin') && (
                        <button
                          disabled={flAiLoading}
                          onClick={() => handleFlAiSummary()}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#7c3aed', color: 'white', fontSize: 12, fontWeight: 600, cursor: flAiLoading ? 'default' : 'pointer', opacity: flAiLoading ? 0.7 : 1 }}
                        >
                          {flAiLoading ? 'Analyzing…' : flAiSummary ? '✦ Re-analyze' : '✦ Analyze this report'}
                        </button>
                      )}
                      {!isPM && canManageQapi && (
                        <>
                          <button
                            onClick={() => setFlEditEntry(e)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'none', color: 'var(--text-muted)', fontSize: '0.8rem', cursor: 'pointer' }}
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button
                            onClick={() => setFlDeleteId(e.id)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.3)', background: 'none', color: '#ef4444', fontSize: '0.8rem', cursor: 'pointer' }}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
                </div>
              </>,
              document.body
            )}
          </div>
        );
      })()}

      {addQapiOpen && (
        <AddQapiModal
          onClose={() => setAddQapiOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['qapi-entries'] });
            queryClient.invalidateQueries({ queryKey: ['completed-projects-qapi'] });
            setAddQapiOpen(false);
          }}
        />
      )}

      {editQapiEntry && (
        <AddQapiModal
          entryToEdit={editQapiEntry}
          onClose={() => setEditQapiEntry(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['qapi-entries'] });
            queryClient.invalidateQueries({ queryKey: ['completed-projects-qapi'] });
            setEditQapiEntry(null);
          }}
        />
      )}

      {deleteConfirmId !== null && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setDeleteConfirmId(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, width: '90vw' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete QAPI Entry?</h3>
              <button className="modal-close" onClick={() => setDeleteConfirmId(null)}><X size={18} /></button>
            </div>
            <div style={{ padding: '16px 0 4px' }}>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
                <button
                  className="btn"
                  style={{ background: '#ef4444', color: '#fff' }}
                  onClick={() => handleDeleteQapi(deleteConfirmId)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {editProjectId !== null && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setEditProjectId(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360, width: '90vw' }}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Completion Date</h3>
              <button className="modal-close" onClick={() => setEditProjectId(null)}><X size={18} /></button>
            </div>
            <div style={{ padding: '16px 0 4px' }}>
              <div className="form-group">
                <label className="form-label">Completed on</label>
                <DatePicker
                  value={editProjectDate}
                  onChange={(v) => setEditProjectDate(v)}
                  placeholder="Select a date"
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setEditProjectId(null)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  disabled={editProjectSaving || !editProjectDate}
                  onClick={async () => {
                    if (!editProjectDate || editProjectId === null) return;
                    setEditProjectSaving(true);
                    try {
                      await apiClient.patch(`/reports/qapi/project/${editProjectId}/completed-date`, { completed_date: editProjectDate });
                      queryClient.invalidateQueries({ queryKey: ['completed-projects-qapi'] });
                      setEditProjectId(null);
                    } catch {
                      /* error silently ignored; modal stays open */
                    } finally {
                      setEditProjectSaving(false);
                    }
                  }}
                >
                  {editProjectSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Fire Logbook: Add / Edit modal ──────────────────────────────────── */}
      {(flAddOpen || flEditEntry != null) && (
        <FlLogbookFormModal
          entryToEdit={flEditEntry}
          initialSection={flAddSection}
          onClose={() => { setFlAddOpen(false); setFlEditEntry(null); }}
          onSaved={(sectionNum, newId) => {
            queryClient.invalidateQueries({ queryKey: ['fire-logbook'] });
            setSelectedFlSection(sectionNum);
            setFlAddOpen(false);
            setFlEditEntry(null);
            if (newId) setFlPdfUploadId(newId);
          }}
        />
      )}

      {/* ── Fire Logbook: Delete confirm ─────────────────────────────────────── */}
      {flDeleteId !== null && ReactDOM.createPortal(
        <div className="modal-overlay"
          onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setFlDeleteId(null); mouseDownTarget.current = null; }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, width: '90vw' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Logbook Entry?</h3>
              <button className="modal-close" onClick={() => setFlDeleteId(null)}><X size={18} /></button>
            </div>
            <div style={{ padding: '16px 0 4px' }}>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setFlDeleteId(null)}>Cancel</button>
                <button
                  className="btn"
                  style={{ background: '#ef4444', color: '#fff' }}
                  disabled={flDeleting}
                  onClick={async () => {
                    setFlDeleting(true);
                    try {
                      await apiClient.delete(`/fire-logbook/${flDeleteId}`);
                      queryClient.invalidateQueries({ queryKey: ['fire-logbook'] });
                      setFlDeleteId(null);
                    } catch { /* ignore */ }
                    finally { setFlDeleting(false); }
                  }}
                >
                  {flDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Fire Logbook: PDF upload modal ───────────────────────────────────── */}
      {flPdfUploadId !== null && (
        <FlPdfUploadModal
          entryId={flPdfUploadId}
          onClose={() => setFlPdfUploadId(null)}
          onUploaded={() => {
            queryClient.invalidateQueries({ queryKey: ['fire-logbook'] });
            setFlPdfUploadId(null);
          }}
        />
      )}

      {printModalOpen && (() => {
        const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const monthLabel = filterMonth > 0 ? MONTH_NAMES[filterMonth - 1] : 'All Months';
        const totalTickets = reportForm.preventative + reportForm.regulatory + reportForm.safety + reportForm.other;
        // Filtering is done server-side; manualEntries is already filtered
        const filteredForPrint = manualEntries;
        const filteredProjectsForPrint = completedProjects.filter(p => {
          const dateStr = p.completed_at ?? p.updated_at;
          if (!dateStr) return true;
          const d = new Date(dateStr);
          if (filterYear && d.getUTCFullYear() !== filterYear) return false;
          if (filterMonth > 0 && d.getUTCMonth() + 1 !== filterMonth) return false;
          return true;
        });
        return ReactDOM.createPortal(
          <div className="modal-overlay"
            onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
            onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setPrintModalOpen(false); mouseDownTarget.current = null; }}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500, width: '90vw' }}>
              <div className="modal-header">
                <h3 className="modal-title">Generate QAPI Report — {monthLabel} {filterYear}</h3>
                <button className="modal-close" onClick={() => setPrintModalOpen(false)}><X size={18} /></button>
              </div>
              <div style={{ padding: '8px 0 4px' }}>
                <p style={{ margin: '0 0 16px 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Enter ticket counts for this period</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                  {([
                    { key: 'preventative', label: 'Preventative maintenance tickets closed' },
                    { key: 'regulatory',   label: 'Regulatory compliance tickets closed' },
                    { key: 'safety',       label: 'Safety tickets closed' },
                    { key: 'other',        label: 'Other maintenance tickets closed' },
                  ] as { key: keyof typeof reportForm; label: string }[]).map(({ key, label }) => (
                    <div key={key} className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>{label}</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={reportForm[key]}
                        onChange={e => setReportForm(f => ({ ...f, [key]: Math.max(0, parseInt(e.target.value) || 0) }))}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 20, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Total: <strong style={{ color: 'var(--text-primary)' }}>{totalTickets}</strong> tickets closed &nbsp;·&nbsp; <strong style={{ color: 'var(--text-primary)' }}>{filteredForPrint.length}</strong> QAPI entries &nbsp;·&nbsp; <strong style={{ color: 'var(--text-primary)' }}>{filteredProjectsForPrint.length}</strong> projects
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => setPrintModalOpen(false)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      generateQapiPDF(filteredForPrint, reportForm, filterMonth, filterYear, filteredProjectsForPrint);
                      setPrintModalOpen(false);
                    }}
                  >
                    Generate PDF
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
}
